/**
 * AppShell — the signed-in frame for candidate-web: the warm paper world (the two radial washes
 * live on `body`), a thread-mark topbar with the account affordance, and a mobile-first single
 * column that widens to a centred max-width on desktop (DESIGN-NOTES delta d — the phone app becomes
 * a centred column, not a full-bleed desktop layout).
 *
 * `title` names the surface in the topbar (design-spec/05: "Ledger"). `footer` is the bottom-anchored
 * region — on the ledger it holds the persistent "Request a reference" CTA, kept in the thumb zone
 * (design law: primary action bottom-anchored, a labelled button, never a FAB).
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { ThreadMark } from './ThreadMark';

interface AppShellProps {
  title: string;
  children: ReactNode;
  footer?: ReactNode;
}

const COLUMN = 'mx-auto w-full max-w-[520px] px-5';

function AccountButton() {
  // No candidate profile endpoint exists at Phase 1, so the topbar avatar is a neutral account
  // affordance (a person glyph), not fabricated initials. Settings lands in WP-F8b.
  return (
    <Link
      to="/settings"
      aria-label="Account and settings"
      className="grid h-[34px] w-[34px] place-items-center rounded-full bg-[linear-gradient(140deg,#7A9278,#5E7860)] text-white transition-[filter] duration-ui ease-thread hover:brightness-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-deep/25 focus-visible:ring-offset-2 focus-visible:ring-offset-paper"
    >
      <svg viewBox="0 0 24 24" fill="none" width={18} height={18} aria-hidden="true">
        <circle cx="12" cy="8" r="3.5" stroke="currentColor" strokeWidth="1.8" />
        <path d="M5 20c0-3.3 3.1-5 7-5s7 1.7 7 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    </Link>
  );
}

export function AppShell({ title, children, footer }: AppShellProps) {
  return (
    <div className="flex min-h-svh flex-col">
      <header className={cn(COLUMN, 'pt-[max(14px,env(safe-area-inset-top))]')}>
        <div className="flex items-center justify-between py-3.5">
          <div className="flex items-center gap-2">
            <ThreadMark className="h-5 w-auto" />
            <span className="font-display text-[16px] font-semibold tracking-[-0.01em] text-ink">
              {title}
            </span>
          </div>
          <AccountButton />
        </div>
      </header>

      {/* keyed remount is not needed here (the ledger is one screen); the mount-in replays on load. */}
      <main className={cn('shell-mount flex flex-1 flex-col', COLUMN)}>{children}</main>

      {footer && (
        <div className="sticky bottom-0 z-10 bg-gradient-to-t from-paper via-paper to-transparent pb-[max(16px,env(safe-area-inset-bottom))] pt-3">
          <div className={COLUMN}>{footer}</div>
        </div>
      )}
    </div>
  );
}
